import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  DestroyRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseComponent } from '../../../../models/base/base-component';
import { TabBarItem } from './tab-bar-item';
import { BehaviorSubject, combineLatest, from, Observable, Subject } from 'rxjs';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { StringExtensions } from '../../../../utils/string.extensions';
import { ScreenService } from '../../../../services/screen-service.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TrackByUtils } from '../../../../utils/track-by-utils';

@Component({
  standalone: true,
  imports: [CommonModule],
  selector: 'app-tab-bar',
  templateUrl: './tab-bar.component.html',
  styleUrls: ['./tab-bar.component.scss']
})
export class TabBarComponent extends BaseComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() public tabs: TabBarItem[] = [];
  @Input() public skipFragmentAppending: boolean = false;
  @Input() public tabChangeGuard?: () => boolean | Promise<boolean> | Observable<boolean>;
  @Output() public selectedTabIndex = new EventEmitter<number>(true);
  @Output() public previousTabIndex = new EventEmitter<number>(true);
  @Output() public selectedTab = new EventEmitter<TabBarItem>(true);

  constructor(private cdr: ChangeDetectorRef, private router: Router) {
    super();
  }

  @ViewChild('tabBar') tabBar: ElementRef | undefined;

  private activatedRoute = inject(ActivatedRoute);
  private screenService = inject(ScreenService);
  private destroyRef = inject(DestroyRef);

  public isMobile$ = this.screenService.isMobile$;

  public currentSelectedTab: number = 0;

  // Track overflow state
  public isOverflowing: boolean = false;
  private resizeObserver?: ResizeObserver;
  private boundCheckOverflow = () => this.checkOverflow();

  private _appendFragmentToUrl = new Subject<TabBarItem>();

  private _tabs = new BehaviorSubject<TabBarItem[]>([]);
  public readonly tabs$ = this._tabs as Observable<TabBarItem[]>;

  private listenToRoute = combineLatest([
    this.activatedRoute.fragment.pipe(distinctUntilChanged()),
    this.tabs$.pipe(filter(tabs => !!tabs && tabs.length > 0)).distinctUniquelyIdentifiableArray()
  ]).subscribeWhileAlive({
    owner: this,
    next: ([fragment, tabs]) => {
      let index = 0;
      if (!!fragment) {
        index = tabs.findIndex(tab => StringExtensions.camelize(tab.title) === fragment);
      }
      this.tabSelected(index);
    }
  });

  private listenToAppendToFrag = this._appendFragmentToUrl
    .pipe(distinctUntilChanged(), takeUntil(this.onDestroy))
    .subscribe(tab => {
      this.appendFragmentToUrl(tab);
    });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tabs) {
      this.setupViews();
      this._tabs.next(this.tabs);
      setTimeout(() => this.checkOverflow());
    }
  }

  ngAfterViewInit(): void {
    this.setupOverflowObservation();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.boundCheckOverflow);
  }

  private setupOverflowObservation() {
    if (!this.tabBar) return;
    const host = this.tabBar.nativeElement as HTMLElement;
    const container = host.querySelector('.tab-container') as HTMLElement | null;
    if (!container) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.checkOverflow();
    });
    this.resizeObserver.observe(host);
    this.resizeObserver.observe(container);
    window.addEventListener('resize', this.boundCheckOverflow, { passive: true });
    setTimeout(() => this.checkOverflow());
  }

  private checkOverflow() {
    if (!this.tabBar) return;
    const host = this.tabBar.nativeElement as HTMLElement;
    const container = host.querySelector('.tab-container') as HTMLElement | null;
    if (!container) return;
    const wasOverflowing = this.isOverflowing;
    // Tolerance of 1px to avoid flicker due to rounding
    this.isOverflowing = container.scrollWidth > host.clientWidth + 1;
    if (wasOverflowing !== this.isOverflowing) {
      this.cdr.markForCheck();
    }
  }

  setupBindings() {}

  setupViews() {
    const selected = this.tabs?.findIndex(tab => tab.active);
    if (selected > -1) {
      this.tabSelected(selected);
    } else {
      this.tabSelected(0);
    }
  }

  public onTabClicked(index: number): void {
    if (index === this.currentSelectedTab) {
      return;
    }
    const guardResult$ = this.tabChangeGuard ? this.tabChangeGuard() : true;
    if (guardResult$ === true) {
      this.tabSelected(index);
      return;
    }
    if (guardResult$ === false) {
      return;
    }
    from(guardResult$)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) {
          this.tabSelected(index);
        }
      });
  }

  public tabSelected(index: number): void {
    if (index === null || index === undefined || index === -1) return;
    this.selectedTabIndex.emit(index);
    this.currentSelectedTab = index;
    this.tabs?.map(t => (t.active = false));
    if (!!this.tabs && this.tabs?.length > this.currentSelectedTab) {
      this.tabs[this.currentSelectedTab].active = true;
      this.selectedTab.emit(this.tabs[this.currentSelectedTab]);
      this._appendFragmentToUrl.next(this.tabs[this.currentSelectedTab]);
    }
    this.cdr.detectChanges();
    setTimeout(() => this.checkOverflow());
  }

  private appendFragmentToUrl(tab: TabBarItem) {
    if (!this.skipFragmentAppending) {
      const frag = StringExtensions.camelize(tab.title);
      const navigationExtras: NavigationExtras = { replaceUrl: true, fragment: frag };
      const current = this.router.parseUrl(this.router.url);
      this.router.navigate(current.root.segments, navigationExtras).then();
    }
  }

  public trackByIndex = TrackByUtils.trackByIndex;
}
